import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { updateFinding } from '../api/client';

const SOURCE_LABEL = { agent1: 'AI Analysis', dre: 'Rules Engine', synthesis: 'Cross-reference' };
const FLAG_COLORS  = { red: '#C8402F', amber: '#D89713', green: '#2F8E54' };

export default function FlagCard({ finding, onUpdate, onToggleInclude }) {
  const [status, setStatus] = useState(finding.reviewer_status || 'pending');
  const [note, setNote]     = useState(finding.reviewer_note || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [included, setIncluded] = useState(finding.include_in_report !== 0);
  const [flagType, setFlagType] = useState(finding.flag_type);

  const borderColor = FLAG_COLORS[flagType] || '#6B7280';
  const curatable = flagType === 'red' || flagType === 'amber';
  const overridden = !!finding.reviewer_flag_type;

  async function changeFlag(val) {
    if (val === flagType) return;
    const prev = flagType;
    setFlagType(val);                         // reflect immediately
    try {
      const updated = await updateFinding(finding.id, { flag_type: val });
      if (onUpdate) onUpdate(updated);
    } catch {
      setFlagType(prev);                      // revert on failure
      alert('Could not change the flag — check the backend is running.');
    }
  }

  function toggleInclude(e) {
    const val = e.target.checked;
    setIncluded(val);
    updateFinding(finding.id, { include_in_report: val ? 1 : 0 }).catch(() => {});
    if (onToggleInclude) onToggleInclude(finding.id, finding.flag_type, val);
  }

  async function save() {
    setSaving(true);
    try {
      const updated = await updateFinding(finding.id, { reviewer_status: status, reviewer_note: note });
      if (onUpdate) onUpdate(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      alert('Could not save — check the backend is running and try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      style={{
        background: '#FFFFFF',
        borderRadius: 16,
        border: '1px solid var(--border)',
        borderLeft: `4px solid ${borderColor}`,
        boxShadow: 'var(--shadow)',
        marginBottom: 12, overflow: 'hidden',
      }}
      whileHover={{ y: -2, boxShadow: '0 10px 30px rgba(60,50,20,0.12)' }}
    >
      <div style={{ padding: '20px 24px' }}>
        {/* Source tag + meta */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{
            background: 'rgba(19,19,19,0.05)', color: 'var(--text-label)',
            fontSize: '0.75rem', fontWeight: 500,
            padding: '3px 10px', borderRadius: 20,
          }}>
            {SOURCE_LABEL[finding.source] || finding.source}
          </span>
          {finding.doc_type && (
            <span style={{
              background: 'rgba(19,19,19,0.05)', color: 'var(--text-label)',
              fontSize: '0.75rem', fontWeight: 500,
              padding: '3px 10px', borderRadius: 20,
            }}>
              {finding.doc_type}
            </span>
          )}
          {curatable && (
            <label style={{
              marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-label)', cursor: 'pointer',
              userSelect: 'none',
            }}>
              <input type="checkbox" checked={included} onChange={toggleInclude}
                     style={{ width: 15, height: 15, accentColor: borderColor, cursor: 'pointer' }} />
              In report
            </label>
          )}
        </div>

        {/* Title */}
        <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', marginBottom: 6, lineHeight: 1.4 }}>
          {finding.title}
        </h3>

        {/* Description */}
        <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.65 }}>
          {finding.description}
        </p>

        {/* Exact text quote */}
        {finding.exact_text && (
          <blockquote style={{
            margin: '14px 0 0', padding: '10px 16px',
            background: 'rgba(19,19,19,0.04)', borderRadius: 8,
            borderLeft: '3px solid var(--border)',
            fontSize: '0.85rem', color: 'var(--text-secondary)',
            overflowWrap: 'anywhere',
            fontStyle: 'italic', lineHeight: 1.6,
          }}>
            "{finding.exact_text}"
          </blockquote>
        )}

        {/* Page / file reference */}
        <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {finding.document_id && (
            <a
              href={`http://localhost:5001/api/documents/${finding.document_id}/file${finding.page_number > 0 ? `#page=${finding.page_number}` : ''}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 12, color: '#6366F1', textDecoration: 'none', fontWeight: 500, overflowWrap: 'anywhere' }}
            >
              📄 {finding.source_filename || 'View document'}
              {finding.page_number > 0 ? ` · p.${finding.page_number}` : ''} →
            </a>
          )}
          {!finding.document_id && finding.source_filename && (
            <span style={{ fontSize: 12, color: 'var(--text-label)' }}>
              📄 {finding.source_filename}
            </span>
          )}
          {finding.confidence_score != null && (
            <span style={{ fontSize: 12, color: 'var(--text-label)' }}>
              {Math.round(finding.confidence_score * 100)}% confidence
            </span>
          )}
        </div>
      </div>

      {/* Reviewer controls */}
      <div style={{
        padding: '12px 24px', background: 'rgba(19,19,19,0.03)',
        borderTop: '1px solid var(--border)',
        display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
             title={overridden ? 'Flag manually set by reviewer' : 'Flag set by AI / rules'}>
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-label)' }}>Flag</span>
          <div style={{ display: 'inline-flex', border: '1px solid var(--border)',
                        borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
            {['red', 'amber', 'green'].map((c, i) => {
              const active = flagType === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => changeFlag(c)}
                  style={{
                    border: 'none',
                    borderLeft: i ? '1px solid var(--border)' : 'none',
                    cursor: 'pointer',
                    padding: '5px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    background: active ? FLAG_COLORS[c] : 'transparent',
                    color: active ? '#fff' : 'var(--text-label)',
                    transition: 'background 0.15s ease',
                  }}
                >
                  {c}
                </button>
              );
            })}
          </div>
          {overridden && <span style={{ fontSize: 11, color: 'var(--text-label)' }}>· edited</span>}
        </div>
        <select
          className="form-control"
          style={{ width: 'auto', fontSize: 13, padding: '6px 12px', borderRadius: 8 }}
          value={status}
          onChange={e => setStatus(e.target.value)}
        >
          <option value="pending">Pending review</option>
          <option value="confirmed">Confirmed</option>
          <option value="dismissed">Dismissed</option>
        </select>
        <input
          className="form-control"
          style={{ flex: 1, minWidth: 180, fontSize: 13, padding: '6px 12px', borderRadius: 8 }}
          placeholder="Add a note..."
          value={note}
          onChange={e => setNote(e.target.value)}
        />
        <button className="btn btn-secondary btn-sm" onClick={save} disabled={saving}
                style={saved ? { color: 'var(--green)', borderColor: 'var(--green)' } : undefined}>
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save'}
        </button>
      </div>
    </motion.div>
  );
}
